import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { motion } from "framer-motion";
import { FaInfoCircle, FaPlus, FaThLarge, FaList, FaFilePdf, FaFileExcel, FaDownload } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import ContractCard from "../contract-card/ContractCard";
import Pagination from "../pagination/Pagination";
import ContractSearch from "./ContractSearch";
import ExpiredContractsModal from "./ExpiringContractsModal";
import ContractDetailsModal from "./ContractDetailsModal";
import ContractsTable from "./ContractsTable";
import ContractsGrid from "./ContractsGrid";
import { Link } from "react-router-dom";

const statusColors = {
  Active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Inactive: "bg-gray-100 text-gray-800 border-gray-200",
  Expired: "bg-rose-100 text-rose-800 border-rose-200",
  Extension: "bg-cyan-100 text-cyan-800 border-cyan-200",
  Support: "bg-amber-100 text-amber-800 border-amber-200",
  Terminated: "bg-orange-100 text-orange-800 border-orange-200",
  Archived: "bg-violet-100 text-violet-800 border-violet-200",
};

const Contracts = () => {
  const [contracts, setContracts] = useState([]);
  const { authState } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedContract, setSelectedContract] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExpiringModalOpen, setIsExpiringModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  const itemsPerPage = 9;

  useEffect(() => {
    const fetchContracts = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/bulk/contracts`,
          {
            headers: {
              "x-auth-token": authState.token,
            },
          }
        );
        setContracts(response.data);
        setFilteredContracts(response.data);
      } catch (error) {
        toast.error(
          error.response?.data?.message || "An unexpected error occurred ⛔⛔"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchContracts();
  }, [authState.token]);

  useEffect(() => {
    let filteredData = [...contracts];
    if (categoryFilter) {
      filteredData = filteredData.filter(
        (contract) => contract.category === categoryFilter
      );
    }
    if (statusFilter) {
      filteredData = filteredData.filter(
        (contract) => contract.status === statusFilter
      );
    }
    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filteredData = filteredData.filter((contract) =>
        JSON.stringify(contract).toLowerCase().includes(lowerCaseSearchTerm)
      );
    }
    setFilteredContracts(filteredData);
    setCurrentPage(1);
  }, [contracts, categoryFilter, statusFilter, searchTerm]);

  const indexOfLastContract = currentPage * itemsPerPage;
  const indexOfFirstContract = indexOfLastContract - itemsPerPage;
  const currentContracts = filteredContracts.slice(
    indexOfFirstContract,
    indexOfLastContract
  );

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const toggleModal = (contract) => {
    setSelectedContract(contract);
    setIsModalOpen(!isModalOpen);
  };

  const handleUpdate = async () => {
    try {
      toast.success("Contract updated successfully");
      toggleModal(null);
    } catch (error) {
      toast.error("Failed to update contract");
    }
  };

  const handleDelete = async () => {
    try {
      toast.success("Contract deleted successfully");
      toggleModal(null);
    } catch (error) {
      toast.error("Failed to delete contract");
    }
  };

  const handleDownloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/reports/contracts/pdf`,
        {
          headers: {
            "x-auth-token": authState.token,
          },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `contracts-report-${new Date().toISOString().split("T")[0]}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("PDF report downloaded successfully");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to download PDF report"
      );
    } finally {
      setDownloadingPDF(false);
    }
  };

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/reports/contracts/excel`,
        {
          headers: {
            "x-auth-token": authState.token,
          },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `contracts-report-${new Date().toISOString().split("T")[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Excel report downloaded successfully");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to download Excel report"
      );
    } finally {
      setDownloadingExcel(false);
    }
  };

  const isAdmin = authState.user.role.includes("admin");

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen mt-14 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6">
          <div className="space-y-4">
            {/* Icon-only row for small devices */}
            <div className="flex justify-between items-center sm:hidden flex-wrap gap-2">
              <Link to="/create-contract">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="p-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-full shadow-md"
                >
                  <FaPlus />
                </motion.button>
              </Link>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="p-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-full shadow-md"
                onClick={() => setIsExpiringModalOpen(true)}
              >
                <FaInfoCircle />
              </motion.button>
              {isAdmin && (
                <>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="p-2 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-full shadow-md"
                    onClick={handleDownloadPDF}
                    disabled={downloadingPDF}
                  >
                    {downloadingPDF ? <FaDownload className="animate-pulse" /> : <FaFilePdf />}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="p-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-full shadow-md"
                    onClick={handleDownloadExcel}
                    disabled={downloadingExcel}
                  >
                    {downloadingExcel ? <FaDownload className="animate-pulse" /> : <FaFileExcel />}
                  </motion.button>
                </>
              )}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className={`p-2 rounded-full shadow-md ${
                  viewMode === "grid"
                    ? "bg-gradient-to-r from-teal-500 to-cyan-600 text-white"
                    : "bg-gray-200 text-gray-700"
                }`}
                onClick={() => setViewMode("grid")}
              >
                <FaThLarge />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className={`p-2 rounded-full shadow-md ${
                  viewMode === "list"
                    ? "bg-gradient-to-r from-teal-500 to-cyan-600 text-white"
                    : "bg-gray-200 text-gray-700"
                }`}
                onClick={() => setViewMode("list")}
              >
                <FaList />
              </motion.button>
            </div>

            {/* Original single-row layout for larger devices */}
            <div className="hidden sm:flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0 md:space-x-4">
              <div className="flex flex-row flex-wrap gap-2 w-full sm:w-auto">
                <Link to="/create-contract" className="w-full sm:w-auto">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-4 py-2 rounded-md flex items-center justify-center sm:justify-start shadow-md transition duration-300 w-full sm:w-auto"
                  >
                    <FaPlus className="mr-2" /> New
                  </motion.div>
                </Link>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-amber-500 to-orange-600 text-white px-4 py-2 rounded-md flex items-center justify-center sm:justify-start shadow-md transition duration-300 w-full sm:w-auto"
                  onClick={() => setIsExpiringModalOpen(true)}
                >
                  <FaInfoCircle className="mr-2" /> Expiring
                </motion.button>

                {isAdmin && (
                  <>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-gradient-to-r from-red-500 to-rose-600 text-white px-4 py-2 rounded-md flex items-center justify-center sm:justify-start shadow-md transition duration-300 w-full sm:w-auto"
                      onClick={handleDownloadPDF}
                      disabled={downloadingPDF}
                    >
                      {downloadingPDF ? (
                        <>
                          <FaDownload className="mr-2 animate-pulse" /> Downloading...
                        </>
                      ) : (
                        <>
                          <FaFilePdf className="mr-2" /> PDF
                        </>
                      )}
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-md flex items-center justify-center sm:justify-start shadow-md transition duration-300 w-full sm:w-auto"
                      onClick={handleDownloadExcel}
                      disabled={downloadingExcel}
                    >
                      {downloadingExcel ? (
                        <>
                          <FaDownload className="mr-2 animate-pulse" /> Downloading...
                        </>
                      ) : (
                        <>
                          <FaFileExcel className="mr-2" /> Excel
                        </>
                      )}
                    </motion.button>
                  </>
                )}
              </div>
              <ContractSearch
                onSearch={setSearchTerm}
                onFilterByCategory={setCategoryFilter}
                onFilterByStatus={setStatusFilter}
                contracts={contracts}
              />
              <div className="flex items-center space-x-2 w-full sm:w-auto justify-end">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`p-2 rounded-md transition duration-300 ${
                    viewMode === "grid"
                      ? "bg-gradient-to-r from-teal-500 to-cyan-600 text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                  onClick={() => setViewMode("grid")}
                >
                  <FaThLarge />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`p-2 rounded-md transition duration-300 ${
                    viewMode === "list"
                      ? "bg-gradient-to-r from-teal-500 to-cyan-600 text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                  onClick={() => setViewMode("list")}
                >
                  <FaList />
                </motion.button>
              </div>
            </div>

            {/* ContractSearch component for small screens */}
            <div className="sm:hidden w-full">
              <ContractSearch
                onSearch={setSearchTerm}
                onFilterByCategory={setCategoryFilter}
                onFilterByStatus={setStatusFilter}
                contracts={contracts}
              />
            </div>
          </div>
        </div>

        {viewMode === "grid" ? (
          <ContractsGrid
            currentContracts={currentContracts}
            isAdmin={isAdmin}
            toggleModal={toggleModal}
            setSelectedContract={setSelectedContract}
            statusColors={statusColors}
          />
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <ContractsTable
              currentContracts={currentContracts}
              statusColors={statusColors}
              setSelectedContract={setSelectedContract}
              toggleModal={toggleModal}
              isAdmin={isAdmin}
            />
          </div>
        )}

        <div className="mt-6">
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(filteredContracts.length / itemsPerPage)}
            onPageChange={paginate}
          />
        </div>

        <ExpiredContractsModal
          isOpen={isExpiringModalOpen}
          onClose={() => setIsExpiringModalOpen(false)}
          authToken={authState.token}
        />

        {selectedContract && !isModalOpen && (
          <ContractCard
            contract={selectedContract}
            onClose={() => setSelectedContract(null)}
          />
        )}

        {selectedContract && isModalOpen && (
          <ContractDetailsModal
            isOpen={isModalOpen}
            onClose={() => toggleModal(null)}
            contract={selectedContract}
            authToken={authState.token}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
          />
        )}
      </div>
    </div>
  );
};

export default Contracts;
